import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWeldData, fetchWeldPasses, clearWeldPasses, fetchZoneView, clearZoneView, fetchTiltView, clearTiltView, fetchPassViewDetails, clearPassViewDetails, fetchWeldSegmentView, clearWeldSegmentView, fetchWeldSegmentDetail, clearWeldSegmentDetail, fetchZoneViewDetails, clearZoneViewDetails, fetchTiltViewDetails, setCurrentProject, clearCurrentProject, fetchProjects, generateReport, clearReportStatus, clearWeldData } from '../actions';
import { useProjectManager } from './useProjectManager';

export const useWeldData = () => {
  const dispatch = useDispatch();
  const { weldData, loading, error, currentProject, projects, projectsLoading, projectsError, selectedProject } = useSelector(state => state.weldData);
  const { projectId, projectName, isProjectReady, findProjectByIdentifier, findProjectByNameAndNumber } = useProjectManager();

  // Projects are loaded externally, no need to auto-load here
  // const loadProjects = useCallback(() => { 
  //   dispatch(fetchProjects());
  // }, [dispatch]);

  // Find weld by weld number
  const findWeldByNumber = useCallback((weldNumber) => {
    if (!weldData || weldData.length === 0) { 
      return null;
    }

    
    // Search for weld by exact weld number match
    const weld = weldData.find(w => w.weldNumber === weldNumber);
    if (weld) {
      return weld;
    }
    
    return null;
  }, [weldData]);

  // These functions are now provided by useProjectManager

  // Projects are loaded externally, no automatic loading needed
  // useEffect(() => { 
  //   if (!projectsLoading && projects.length === 0) {
  //     loadProjects();
  //   } else if (projects.length > 0) {
  //   } else if (projectsLoading) {
  //     loadProjects();
  //   }
  // }, [projects.length, projectsLoading, loadProjects]);

  const loadWeldData = useCallback((jobNumber = null, pipeId = null) => { 
    if (!isProjectReady || !projectId || !projectName) {
      return;
    }
    // pipeId may be a single id or an array of pipe ids (multi-pipe fan-out).
    dispatch(fetchWeldData(projectId, projectName, jobNumber, pipeId));
  }, [dispatch, projectId, projectName, isProjectReady]);

  // These functions are now handled by individual hooks (useWeldPasses, useZoneView, etc.)

  const clearWeldDataAction = useCallback(() => {
    dispatch(clearWeldData());
  }, [dispatch]);

  return {
    weldData,
    loading,
    error,
    currentProject,
    projects,
    projectsLoading,
    projectsError,
    selectedProject,
    loadWeldData,
    clearWeldData: clearWeldDataAction,
    findProjectByIdentifier,
    findProjectByNameAndNumber,
    findWeldByNumber
  };
};

export const useWeldPasses = () => {
  const dispatch = useDispatch();
  const { weldPasses, passesLoading, passesError } = useSelector(state => state.weldData);
  const { projectId, projectName, isProjectReady } = useProjectManager();

  const loadWeldPasses = useCallback((weldNumber) => {
    if (!isProjectReady || !projectId || !projectName) {
      return;
    }
    
    dispatch(fetchWeldPasses(projectId, projectName, weldNumber));
  }, [dispatch, projectId, projectName, isProjectReady]);

  const clearPasses = useCallback(() => {
    dispatch(clearWeldPasses());
  }, [dispatch]);

  return {
    weldPasses,
    passesLoading,
    passesError,
    loadWeldPasses,
    clearPasses
  };
};

export const useZoneView = () => {
  const dispatch = useDispatch();
  const { zoneViewData, zoneViewLoading, zoneViewError } = useSelector(state => state.weldData);
  const { projectId, projectName, isProjectReady } = useProjectManager();

  const loadZoneView = useCallback((weldNumber) => {
    if (!isProjectReady || !projectId || !projectName) {
      return;
    }
    
    dispatch(fetchZoneView(projectId, projectName, weldNumber));
  }, [dispatch, projectId, projectName, isProjectReady]);

  const clearZoneViewData = useCallback(() => {
    dispatch(clearZoneView());
  }, [dispatch]);

  return {
    zoneViewData,
    zoneViewLoading,
    zoneViewError,
    loadZoneView,
    clearZoneViewData
  };
};

export const useTiltView = () => {
  const dispatch = useDispatch();
  const { tiltViewData, tiltViewLoading, tiltViewError } = useSelector(state => state.weldData);
  const { projectId, projectName, isProjectReady } = useProjectManager();

  const loadTiltView = useCallback((weldNumber) => {
    if (!isProjectReady || !projectId || !projectName) {
      return;
    }
    
    dispatch(fetchTiltView(projectId, projectName, weldNumber));
  }, [dispatch, projectId, projectName, isProjectReady]);

  const clearTiltViewData = useCallback(() => {
    dispatch(clearTiltView());
  }, [dispatch]);

  return {
    tiltViewData,
    tiltViewLoading,
    tiltViewError,
    loadTiltView,
    clearTiltViewData
  };
};

export const usePassViewDetails = () => {
  const dispatch = useDispatch();
  const { passViewDetails, passViewDetailsLoading, passViewDetailsError } = useSelector(state => state.weldData);
  const { projectId, projectName, isProjectReady } = useProjectManager();

  const loadPassViewDetails = useCallback((welduid_s_event_torch, viewType = 'Pass', torchType = 'Lead') => {
    if (!isProjectReady || !projectId || !projectName) {
      return;
    }
    
    dispatch(fetchPassViewDetails(projectId, projectName, welduid_s_event_torch, viewType, torchType));
  }, [dispatch, projectId, projectName, isProjectReady]);

  const clearPassViewDetailsData = useCallback(() => {
    dispatch(clearPassViewDetails());
  }, [dispatch]);

  return {
    passViewDetails,
    passViewDetailsLoading,
    passViewDetailsError,
    loadPassViewDetails,
    clearPassViewDetailsData
  };
};

export const useWeldSegmentView = () => {
  const dispatch = useDispatch();
  const { weldSegmentView, weldSegmentViewLoading, weldSegmentViewError } = useSelector(state => state.weldData);
  const { projectId, projectName, isProjectReady } = useProjectManager();

  const loadWeldSegmentView = useCallback((welduid_s_event_torch, viewType = 'Pass', torchType = 'Lead') => {
    if (!isProjectReady || !projectId || !projectName) {
      return;
    }

    dispatch(fetchWeldSegmentView(projectId, projectName, welduid_s_event_torch, viewType, torchType));
  }, [dispatch, projectId, projectName, isProjectReady]);

  const clearWeldSegmentViewData = useCallback(() => {
    dispatch(clearWeldSegmentView());
  }, [dispatch]);

  return {
    weldSegmentView,
    weldSegmentViewLoading,
    weldSegmentViewError,
    loadWeldSegmentView,
    clearWeldSegmentViewData
  };
};

export const useWeldSegmentDetail = () => {
  const dispatch = useDispatch();
  const { weldSegmentDetail, weldSegmentDetailLoading, weldSegmentDetailError } = useSelector(state => state.weldData);
  const { projectId, projectName, isProjectReady } = useProjectManager();

  const loadWeldSegmentDetail = useCallback((welduid_s_event_torch, welduid_t_event_torch, viewType = 'Pass', torchType = 'Lead') => {
    // Always pass both S and T. Empty T is allowed (orphan / T-missing segments).
    // Skip only when both keys are missing.
    const sKey = welduid_s_event_torch == null ? '' : String(welduid_s_event_torch).trim();
    const tKey = welduid_t_event_torch == null ? '' : String(welduid_t_event_torch).trim();
    if (!isProjectReady || !projectId || !projectName || (!sKey && !tKey)) {
      return;
    }

    dispatch(fetchWeldSegmentDetail(projectId, projectName, sKey, tKey, viewType, torchType));
  }, [dispatch, projectId, projectName, isProjectReady]);

  const clearWeldSegmentDetailData = useCallback(() => {
    dispatch(clearWeldSegmentDetail());
  }, [dispatch]);

  return {
    weldSegmentDetail,
    weldSegmentDetailLoading,
    weldSegmentDetailError,
    loadWeldSegmentDetail,
    clearWeldSegmentDetailData
  };
};

export const useZoneViewDetails = () => {
  const dispatch = useDispatch();
  const { zoneViewDetails, zoneViewDetailsLoading, zoneViewDetailsError } = useSelector(state => state.weldData);
  const { projectId, projectName, isProjectReady } = useProjectManager();

  const loadZoneViewDetails = useCallback((welduid_s_event_torch, viewType = 'Zone', torchType = 'Lead', zoneName) => {
    if (!isProjectReady || !projectId || !projectName) {
      return;
    }
    
    dispatch(fetchZoneViewDetails(projectId, projectName, welduid_s_event_torch, viewType, torchType, zoneName));
  }, [dispatch, projectId, projectName, isProjectReady]);

  const clearZoneViewDetailsData = useCallback(() => {
    dispatch(clearZoneViewDetails());
  }, [dispatch]);

  return {
    zoneViewDetails,
    zoneViewDetailsLoading,
    zoneViewDetailsError,
    loadZoneViewDetails,
    clearZoneViewDetailsData
  };
};

export const useTiltViewDetails = () => {
  const dispatch = useDispatch();
  const { passViewDetails, passViewDetailsLoading, passViewDetailsError } = useSelector(state => state.weldData);
  const { projectId, projectName, isProjectReady } = useProjectManager();

  const loadTiltViewDetails = useCallback((welduid_s_event_torch, viewType = 'Tilt', torchType = 'Lead', tiltStart, tiltEnd) => {
    if (!isProjectReady || !projectId || !projectName) {
      return;
    }
    
    dispatch(fetchTiltViewDetails(projectId, projectName, welduid_s_event_torch, viewType, torchType, tiltStart, tiltEnd));
  }, [dispatch, projectId, projectName, isProjectReady]);

  const clearTiltViewDetailsData = useCallback(() => {
    dispatch(clearPassViewDetails()); // Reuse the same action for now
  }, [dispatch]);

  return {
    tiltViewDetails: passViewDetails, // Reuse the same state for now
    tiltViewDetailsLoading: passViewDetailsLoading,
    tiltViewDetailsError: passViewDetailsError,
    loadTiltViewDetails,
    clearTiltViewDetailsData
  };
};

// New hook for managing current project
export const useCurrentProject = () => {
  const dispatch = useDispatch();
  const { currentProject } = useSelector(state => state.weldData);

  const setProject = useCallback((project) => {
    dispatch(setCurrentProject(project));
  }, [dispatch]);

  const clearProject = useCallback(() => {
    dispatch(clearCurrentProject());
  }, [dispatch]);

  return {
    currentProject,
    setProject,
    clearProject
  };
};

// Simple hook to trigger projects loading
export const useProjectsLoader = () => {
  const dispatch = useDispatch();
  const { projects, projectsLoading, projectsError } = useSelector(state => state.weldData);
  const hasLoadedRef = useRef(false);

  const loadProjects = useCallback(() => {
    dispatch(fetchProjects());
  }, [dispatch]);

  // Auto-load projects on mount
  useEffect(() => {
    if (projects.length === 0 && !projectsLoading && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadProjects();
    }
  }, [projects.length, projectsLoading, loadProjects]); // Added loadProjects dependency

  return {
    projects,
    projectsLoading,
    projectsError,
    loadProjects
  };
}; 

// New hook for report generation
export const useReportGeneration = () => {
  const dispatch = useDispatch();
  const { 
    reportGenerationLoading, 
    reportGenerationSuccess, 
    reportGenerationError, 
    reportGenerationData,
    currentProject,
    projects
  } = useSelector(state => state.weldData);

  const generateReportAction = useCallback((reportType, emailAddress, weldNumbers = [], reportVersion = 'v1', projectId = null) => {
    let finalProjectId = projectId;
    //console.log('🔄 useReportGeneration - generateReportAction', reportType, emailAddress, weldNumbers, reportVersion, projectId);
    // // If we have a current project with project number, try to find the data360 live project
    // if (currentProject?.project?.projectNumber && projects && projects.length > 0) {
    //   const projectNumber = currentProject.project.projectNumber;
      
    //   // Find the project in data360 live projects
    //   const data360Project = projects.find(p => p.project_number === projectNumber);
    //   if (data360Project) {
    //     finalProjectId = data360Project.project_id;
    //   }
    // }
    
    // // Use provided projectId or fall back to currentProject
    // if (!finalProjectId) {
    //   finalProjectId = currentProject?.project?.projectId;
    // }
    
    if (!finalProjectId) {
      return;
    }
    
    // Convert emailAddress array to comma-separated string
    const emailString = Array.isArray(emailAddress) ? emailAddress.join(',') : emailAddress;

    // Always use the main generateReport action - it will conditionally include weldNumbers
    dispatch(generateReport(reportType, finalProjectId, emailString, weldNumbers, null, null, reportVersion));
  }, [dispatch, currentProject, projects]);

  const clearReportStatusAction = useCallback(() => {
    dispatch(clearReportStatus());
  }, [dispatch]);

  // Auto-clear success status after 5 seconds
  useEffect(() => {
    if (reportGenerationSuccess) {
      const timer = setTimeout(() => {
        clearReportStatusAction();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [reportGenerationSuccess, clearReportStatusAction]);

  return {
    reportGenerationLoading,
    reportGenerationSuccess,
    reportGenerationError,
    reportGenerationData,
    generateReport: generateReportAction,
    clearReportStatus: clearReportStatusAction
  };
}; 